import { useState } from 'react'

import { RichText } from '~/components/RichText'
import { AuthorCard } from '~/components/Author'
import { TableOfContent } from '../blog/TableOfContent'
import { getTocItems } from '../blog/TableOfContent/utils'
import { Share, ShareRow } from '../blog/Share'

import { Hero } from './Hero'
import { trackEvent } from '~/utils/tracking/trackEvent'
import { Button } from '~/components/general/Button'
import { FlexContainer, FlexItem } from '../Grid'
import classNames from 'classnames'
import { SlideAside } from '../blog/SlideAside'
import { TypeAuthorSkeleton, TypeResumeExample } from '~/types/contentful'
import { Asset } from 'contentful'
import { Entry } from 'contentful'

type ArticleProps = {
  data: TypeResumeExample<'WITHOUT_UNRESOLVABLE_LINKS', string>
  type: 'coverLetter' | 'resume'
}

export const Article = ({ data, type }: ArticleProps) => {
  const { body, author, title, abstract } = data.fields

  const [richTextArticle, setRichTextArticle] = useState<HTMLElement | null>(null)
  const toc = getTocItems(body)

  const examplePreview = data.fields.examplePreview as Asset<'WITHOUT_UNRESOLVABLE_LINKS', string>

  return (
    <div>
      <Hero type={type} />
      <div className="relative py-0 max-xl:px-0 px-25 max-xl:flex max-xl:flex-wrap max-xl:justify-between">
        <div className="absolute left-0 h-full z-1 max-xl:static max-xl:h-auto max-xl:mb-7 max-md:mb-5 max-md:-ml-3">
          <div className="max-xl:static sticky left-0 top-[calc(32px_+_var(--header-height))]">
            {toc && <TableOfContent items={toc} richTextArticle={richTextArticle} />}
          </div>
        </div>
        <div className="absolute right-0 h-full z-1 max-xl:static max-xl:h-auto max-xl:mb-7 max-md:mb-5 max-md:mr-[-10px]">
          <div className="max-xl:static sticky left-0 top-[calc(32px_+_var(--header-height))]">
            <Share articleTitle={title} articleAbstract={abstract} />
          </div>
        </div>
        <FlexContainer justify="between">
          <FlexItem
            className={classNames(
              "relative max-lg:mb-10 after:content-[''] after:w-[calc(100%+32px)] max-lg:after:w-[calc(100%+26px)] max-md:after:w-[calc(100%+18px)] max-lg:after:-left-6 max-md:after:-left-5 after:absolute after:bottom-0 after:h-[80%] after:-left-8",
            )}
            desktop={8}
            laptop={8}
          >
            {abstract && (
              <p className="whitespace-pre-wrap mb-15 max-xl:mb-10 max-md:mb-7 text-m">
                {abstract}
              </p>
            )}
            {body && <RichText doc={body} ref={setRichTextArticle} />}
          </FlexItem>
          <SlideAside className="max-lg:w-full w-[30%]" key={data.sys.id}>
            {author && (
              <AuthorCard
                author={author as Entry<TypeAuthorSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS', string>}
              />
            )}
            <div className="p-6 rounded-3xl bg-neutral-5 [&_img]:w-full">
              {examplePreview && (
                <img
                  src={examplePreview.fields?.file?.url}
                  alt={examplePreview.fields?.file?.fileName}
                />
              )}
              <div className="flex w-full mt-6">
                <Button
                  text={type === 'resume' ? 'Edit This Resume' : 'Edit This Cover Letter'}
                  type={'link'}
                  href={data?.fields?.exampleLink}
                  className="mx-auto"
                  onClick={() => {
                    trackEvent('click_cta_sidebanner', {
                      website_host: 'career.io',
                      label: type === 'resume' ? 'resume_examples' : 'cover_letter_examples',
                      cta: type === 'resume' ? 'edit_resume' : 'edit_cover_letter',
                      url: data?.fields?.exampleLink,
                      type: type,
                    })
                  }}
                />
              </div>
            </div>
          </SlideAside>
        </FlexContainer>
        <FlexContainer>
          <ShareRow articleTitle={title} articleAbstract={abstract} articleId={data.sys.id} />
        </FlexContainer>
      </div>
    </div>
  )
}
